/* Active-role lens. A user may hold several roles (admin / teacher / tutor); */
/* the app shows ONE mode at a time. The active role is a client-side */
/* presentation lens persisted per-user in localStorage — server RLS still */
/* enforces real permissions regardless, so switching mode never grants access */
/* the profile doesn't already have. The switcher (sidebar) lets a multi-role */
/* user flip mode without signing out. */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchRoleAssignments, type RoleAssignmentPayload } from '../lib/roles';
import { api } from '../lib/api';
import { setActiveSchoolId as persistActiveSchoolId } from '../lib/active-school';
import { useAuth } from './use-auth';

export type Role = 'admin' | 'direction' | 'teacher' | 'tutor' | 'eleve' | 'ai_operator';

/* Most → least privileged. Default active role = highest available. */
const ORDER: Role[] = ['admin', 'direction', 'ai_operator', 'teacher', 'tutor', 'eleve'];

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  direction: 'Direction',
  teacher: 'Enseignant',
  tutor: 'Parent / tuteur',
  eleve: 'Élève',
  ai_operator: 'Opérateur IA',
};

export interface LinkedChild {
  readonly classroomStudentId: string;
  readonly firstName: string;
  readonly lastName: string;
  /* Le chat libre s'active enfant par enfant : la navigation en a besoin pour
     ne pas proposer une entrée qui mènerait à une page verrouillée. Vient de
     `myChildren`, déjà chargé ici — aucun appel réseau supplémentaire. */
  readonly studentChatEnabled: boolean;
}

const KNOWN_ROLES: Role[] = ['admin', 'direction', 'teacher', 'tutor', 'eleve', 'ai_operator'];

/** Un item du sélecteur de rôle : un rôle, une école le cas échéant (admin/ai_operator = plateforme). */
export interface RoleAssignment {
  readonly role: Role;
  readonly schoolId: string | null;
  readonly schoolName: string | null;
}

function toRoleAssignments(rows: RoleAssignmentPayload[]): RoleAssignment[] {
  const result: RoleAssignment[] = [];
  for (const row of rows) {
    if (!KNOWN_ROLES.includes(row.role as Role)) continue;
    result.push({ role: row.role as Role, schoolId: row.school_id, schoolName: row.school_name });
  }
  return result;
}

function assignmentKey(a: Pick<RoleAssignment, 'role' | 'schoolId'>): string {
  return `${a.role}:${a.schoolId ?? ''}`;
}

interface ChildPayload {
  classroom_student_id: string;
  first_name: string;
  last_name: string;
  student_chat_enabled_at?: string | null;
}

export function toLinkedChildren(kids: unknown): LinkedChild[] {
  return (kids as ChildPayload[]).map((k) => ({
    classroomStudentId: k.classroom_student_id,
    firstName: k.first_name,
    lastName: k.last_name,
    studentChatEnabled: Boolean(k.student_chat_enabled_at),
  }));
}

interface RolesState {
  loading: boolean;
  /** Types de rôle distincts — pour les gardes de route et l'UI qui ne regardent que le type. */
  available: Role[];
  /** Un item par role_assignment — alimente le sélecteur (badge d'école, multi-item). */
  assignments: RoleAssignment[];
  activeRole: Role | null;
  /** École de l'assignation active (null = admin/ai_operator, scope plateforme). */
  activeSchoolId: string | null;
  activeAssignment: RoleAssignment | null;
  setActiveAssignment: (a: RoleAssignment) => void;
  /** Conservé pour les appelants qui ne connaissent que le type de rôle (pas l'école) — */
  /** choisit la première assignation disponible pour ce rôle. */
  setActiveRole: (r: Role) => void;
  /* Contexte parent/tuteur : enfants liés + enfant actif (aucun rôle DB — dérivé des liens). */
  children: LinkedChild[];
  activeStudentId: string | null;
  setActiveStudent: (id: string) => void;
  /** Recharge les enfants liés — après activation/révocation du chat. */
  refreshChildren: () => Promise<void>;
}

const RolesContext = createContext<RolesState | null>(null);

const storageKey = (uid: string) => `naia.activeAssignment.${uid}`;
const studentKey = (uid: string) => `naia.activeStudent.${uid}`;

export function RolesProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const uid = session?.user?.id ?? null;
  const [available, setAvailable] = useState<Role[]>([]);
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [activeAssignment, setActiveAssignmentState] = useState<RoleAssignment | null>(null);
  const [linkedChildren, setLinkedChildren] = useState<LinkedChild[]>([]);
  const [activeStudentId, setActiveStudentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!uid) {
      setAvailable([]);
      setAssignments([]);
      setActiveAssignmentState(null);
      persistActiveSchoolId(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      /* INVARIANT : `loading` doit retomber quoi qu'il arrive. Une promesse qui
         rejette ici (401, panne réseau, charge utile inattendue) laisserait sinon
         tout consommateur du contexte sur un écran de chargement définitif. */
      try {
        /* Modes = assignations DB (role_assignments + tutor dérivé des enfants, */
        /* tout via roles.listAssignments) + enfants liés (pour la navigation */
        /* tutor). Pas de baseline : un compte sans rôle ni enfant atterrit sur */
        /* /app/pending. Les deux appels se rattrapent séparément : une panne de */
        /* l'un ne doit pas effacer le mode que l'autre suffit à établir. */
        const [rows, kids] = await Promise.all([
          fetchRoleAssignments(uid).catch(() => [] as RoleAssignmentPayload[]),
          api.parentOrTutor.myChildren.query().catch(() => [] as unknown[]),
        ]);
        if (cancelled) return;

        const items = toRoleAssignments(rows);
        const linked = toLinkedChildren(kids);
        /* Filet de secours : si l'appel assignations échoue mais que les enfants
           liés chargent, ne pas priver le parent du mode tuteur (juste sans badge
           d'école — dégradé, pas absent). */
        if (linked.length > 0 && !items.some((a) => a.role === 'tutor')) {
          items.push({ role: 'tutor', schoolId: null, schoolName: null });
        }
        const set = new Set<Role>(items.map((a) => a.role));

        const ordered = ORDER.filter((r) => set.has(r));
        const orderedAssignments = [...items].sort(
          (a, b) => ORDER.indexOf(a.role) - ORDER.indexOf(b.role),
        );
        setAvailable(ordered);
        setAssignments(orderedAssignments);
        setLinkedChildren(linked);

        let initial: RoleAssignment | null = orderedAssignments[0] ?? null;
        try {
          const saved = localStorage.getItem(storageKey(uid));
          if (saved) {
            const parsed = JSON.parse(saved) as { role: Role; schoolId: string | null };
            const match = orderedAssignments.find((a) => assignmentKey(a) === assignmentKey(parsed));
            if (match) initial = match;
          }
        } catch { /* ignore */ }
        setActiveAssignmentState(initial);
        persistActiveSchoolId(initial?.schoolId ?? null);

        if (linked.length > 0) {
          let student = linked[0]!.classroomStudentId;
          try {
            const savedStudent = localStorage.getItem(studentKey(uid));
            if (savedStudent && linked.some((c) => c.classroomStudentId === savedStudent)) {
              student = savedStudent;
            }
          } catch { /* ignore */ }
          setActiveStudentId(student);
        } else {
          setActiveStudentId(null);
        }
      } catch {
        if (cancelled) return;
        setAvailable([]);
        setAssignments([]);
        setActiveAssignmentState(null);
        persistActiveSchoolId(null);
        setLinkedChildren([]);
        setActiveStudentId(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, authLoading]);

  const setActiveAssignment = (a: RoleAssignment) => {
    if (!assignments.some((x) => assignmentKey(x) === assignmentKey(a))) return;
    setActiveAssignmentState(a);
    persistActiveSchoolId(a.schoolId);
    if (uid) {
      try {
        localStorage.setItem(storageKey(uid), JSON.stringify({ role: a.role, schoolId: a.schoolId }));
      } catch { /* ignore */ }
    }
  };

  const setActiveRole = (r: Role) => {
    const match = assignments.find((a) => a.role === r);
    if (match) setActiveAssignment(match);
  };

  /* Appelé par la carte d'activation : sans ça, l'entrée de rail n'apparaîtrait
     qu'au rechargement suivant, `myChildren` n'étant lu qu'au montage. */
  const refreshChildren = async () => {
    const kids = await api.parentOrTutor.myChildren.query().catch(() => null);
    if (kids) setLinkedChildren(toLinkedChildren(kids));
  };

  const setActiveStudent = (id: string) => {
    if (!linkedChildren.some((c) => c.classroomStudentId === id)) return;
    setActiveStudentId(id);
    if (uid) { try { localStorage.setItem(studentKey(uid), id); } catch { /* ignore */ } }
  };

  const value = useMemo<RolesState>(
    () => ({
      loading,
      available,
      assignments,
      activeRole: activeAssignment?.role ?? null,
      activeSchoolId: activeAssignment?.schoolId ?? null,
      activeAssignment,
      setActiveAssignment,
      setActiveRole,
      children: linkedChildren,
      activeStudentId,
      setActiveStudent,
      refreshChildren,
    }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [loading, available, assignments, activeAssignment, linkedChildren, activeStudentId],
  );

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function PreviewRolesProvider({ role, children }: { role: Role | null; children: ReactNode }) {
  const value = useMemo<RolesState>(() => {
    const assignment: RoleAssignment | null = role ? { role, schoolId: null, schoolName: null } : null;
    return {
      loading: false,
      available: role ? [role] : [],
      assignments: assignment ? [assignment] : [],
      activeRole: role,
      activeSchoolId: null,
      activeAssignment: assignment,
      setActiveAssignment: () => {},
      setActiveRole: () => {},
      children: [],
      activeStudentId: null,
      setActiveStudent: () => {},
      refreshChildren: async () => {},
    };
  }, [role]);
  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function useRoles(): RolesState {
  const ctx = useContext(RolesContext);
  if (!ctx) throw new Error('useRoles must be used within RolesProvider');
  return ctx;
}
